import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { HERO_MODELS } from './heroModels'

/* ---- hero background: a water-ripple liquid surface, reacting to the
   pointer with an occasional ambient drop. Two-buffer (ping-pong)
   height-field simulation over a 256x256 offscreen target, composited
   with specular + fresnel lighting. The "NOW SERVING" model ticker
   cycles on its own timer, independent of the water sim. ---- */
export default function LiquidBackground() {
  const containerRef = useRef(null)
  const [modelIndex, setModelIndex] = useState(0)

  useEffect(() => {
    const container = containerRef.current
    if (!container || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

    let cancelled = false
    let cleanup = () => {}

    ;(async () => {
      const THREE = await import('https://esm.sh/three@0.170.0')
      if (cancelled) return

      const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false })
      renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
      renderer.autoClear = false
      renderer.outputColorSpace = THREE.SRGBColorSpace
      container.appendChild(renderer.domElement)
      renderer.domElement.style.cssText = 'width:100%;height:100%;display:block;'

      // ---------- water simulation ----------
      const SIM = 256, GRID = 22
      const QUAD_V = `varying vec2 vUv; void main(){ vUv=uv; gl_Position=vec4(position,1.0); }`
      const UPDATE_F = `uniform sampler2D uTexture; uniform vec2 uDelta; varying vec2 vUv;
        void main(){ vec4 info=texture2D(uTexture,vUv); vec2 dx=vec2(uDelta.x,0.0); vec2 dy=vec2(0.0,uDelta.y);
        float avg=(texture2D(uTexture,vUv-dx).r+texture2D(uTexture,vUv-dy).r+texture2D(uTexture,vUv+dx).r+texture2D(uTexture,vUv+dy).r)*0.25;
        info.g+=(avg-info.r)*2.0; info.g*=0.986; info.r+=info.g;
        vec2 edge=abs(vUv-0.5)*2.0; float ef=1.0-smoothstep(0.92,1.0,max(edge.x,edge.y)); info.r*=ef; info.g*=ef;
        gl_FragColor=info; }`
      const DROP_F = `uniform sampler2D uTexture; uniform vec2 uCenter; uniform float uRadius; uniform float uStrength; varying vec2 vUv;
        void main(){ vec4 info=texture2D(uTexture,vUv); float d=max(0.0,1.0-length(uCenter-vUv)/uRadius); d=0.5-cos(d*3.14159265)*0.5; info.r+=d*uStrength; gl_FragColor=info; }`
      const RENDER_F = `uniform sampler2D uWater; uniform sampler2D uBackground; uniform vec2 uDelta; uniform vec2 uResolution; uniform float uGridScale; uniform vec3 uGrid; varying vec2 vUv;
        void main(){ vec4 info=texture2D(uWater,vUv);
        float hx=texture2D(uWater,vec2(vUv.x+uDelta.x,vUv.y)).r-info.r; float hy=texture2D(uWater,vec2(vUv.x,vUv.y+uDelta.y)).r-info.r;
        vec3 tx=vec3(uDelta.x,0.0,hx); vec3 ty=vec3(0.0,uDelta.y,hy); vec3 n=normalize(cross(tx,ty));
        vec2 du=vUv+n.xy*0.09; vec3 color=texture2D(uBackground,du).rgb;
        vec3 L=normalize(vec3(-0.4,0.6,0.7)); float spec=pow(max(0.0,dot(reflect(-L,n),vec3(0.0,0.0,1.0))),60.0); float fres=pow(1.0-max(0.0,n.z),2.0);
        color+=vec3(0.85,0.97,1.0)*spec*1.6; color+=uGrid*fres*0.8;
        gl_FragColor=vec4(color,1.0); }`

      const orthoCam = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1)
      const quadScene = new THREE.Scene()
      const quadGeo = new THREE.PlaneGeometry(2, 2)
      const rtOpt = { type: THREE.HalfFloatType, format: THREE.RGBAFormat, minFilter: THREE.LinearFilter, magFilter: THREE.LinearFilter }
      let rtA = new THREE.WebGLRenderTarget(SIM, SIM, rtOpt)
      let rtB = new THREE.WebGLRenderTarget(SIM, SIM, rtOpt)
      ;[rtA, rtB].forEach((rt) => { renderer.setRenderTarget(rt); renderer.clear() })
      renderer.setRenderTarget(null)
      const delta = new THREE.Vector2(1 / SIM, 1 / SIM)
      const gridCol = new THREE.Color(0x21B7CB)

      const updMat = new THREE.ShaderMaterial({ vertexShader: QUAD_V, fragmentShader: UPDATE_F, uniforms: { uTexture: { value: null }, uDelta: { value: delta } } })
      const dropMat = new THREE.ShaderMaterial({ vertexShader: QUAD_V, fragmentShader: DROP_F, uniforms: { uTexture: { value: null }, uCenter: { value: new THREE.Vector2(.5, .5) }, uRadius: { value: .07 }, uStrength: { value: .04 } } })
      const rendMat = new THREE.ShaderMaterial({ vertexShader: QUAD_V, fragmentShader: RENDER_F, uniforms: { uWater: { value: null }, uBackground: { value: null }, uDelta: { value: delta }, uResolution: { value: new THREE.Vector2(1, 1) }, uGridScale: { value: GRID }, uGrid: { value: gridCol } } })
      const quad = new THREE.Mesh(quadGeo, updMat); quadScene.add(quad)

      // background texture: near-black with a soft cyan radial glow low-center
      const bgC = document.createElement('canvas'); bgC.width = bgC.height = 1024
      const bx = bgC.getContext('2d')
      bx.fillStyle = '#000'; bx.fillRect(0, 0, 1024, 1024)
      const rg = bx.createRadialGradient(512, 640, 40, 512, 640, 620)
      rg.addColorStop(0, 'rgba(33,183,203,0.28)'); rg.addColorStop(0.5, 'rgba(20,60,72,0.12)'); rg.addColorStop(1, 'rgba(0,0,0,0)')
      bx.fillStyle = rg; bx.fillRect(0, 0, 1024, 1024)
      const bgTex = new THREE.CanvasTexture(bgC); bgTex.colorSpace = THREE.SRGBColorSpace

      const runPass = (mat, target) => { quad.material = mat; renderer.setRenderTarget(target); renderer.render(quadScene, orthoCam) }
      const swap = () => { const t = rtA; rtA = rtB; rtB = t }
      const pending = []
      const queueDrop = (u, v, r, s) => pending.push({ u, v, r, s })

      let last = null
      function handlePointerMove(e) {
        const rect = renderer.domElement.getBoundingClientRect()
        const u = (e.clientX - rect.left) / rect.width, v = 1 - (e.clientY - rect.top) / rect.height
        if (u < 0 || u > 1 || v < 0 || v > 1) return
        if (last && Math.hypot(u - last.x, v - last.y) < 0.004) return
        last = { x: u, y: v }; queueDrop(u, v, 0.06, 0.03)
      }
      renderer.domElement.addEventListener('pointermove', handlePointerMove)

      function ambientDrop() {
        queueDrop(0.15 + Math.random() * 0.7, 0.15 + Math.random() * 0.7, 0.05 + Math.random() * 0.05, 0.02)
      }
      const ambientInterval = setInterval(ambientDrop, 2600)

      function resize() {
        const w = container.clientWidth, h = container.clientHeight
        if (w === 0 || h === 0) return
        renderer.setSize(w, h)
        rendMat.uniforms.uResolution.value.set(w, h)
      }
      resize()
      const resizeObserver = new ResizeObserver(resize)
      resizeObserver.observe(container)

      function renderFrame() {
        if (cancelled) return
        for (const d of pending.splice(0)) {
          dropMat.uniforms.uTexture.value = rtA.texture
          dropMat.uniforms.uCenter.value.set(d.u, d.v)
          dropMat.uniforms.uRadius.value = d.r; dropMat.uniforms.uStrength.value = d.s
          runPass(dropMat, rtB); swap()
        }
        updMat.uniforms.uTexture.value = rtA.texture; runPass(updMat, rtB); swap()

        rendMat.uniforms.uWater.value = rtA.texture
        rendMat.uniforms.uBackground.value = bgTex
        renderer.setRenderTarget(null)
        renderer.clear(true, true, false)
        runPass(rendMat, null)
      }

      let frameId = 0
      function loop() { frameId = requestAnimationFrame(loop); renderFrame() }
      loop()
      // Belt-and-suspenders redraw so the frame keeps advancing even if rAF throttles
      // (e.g. background-tab edge cases) — carried over from the source bundle.
      const pump = setInterval(renderFrame, 120)

      cleanup = () => {
        resizeObserver.disconnect()
        clearInterval(pump)
        clearInterval(ambientInterval)
        cancelAnimationFrame(frameId)
        renderer.domElement.removeEventListener('pointermove', handlePointerMove)
        renderer.dispose()
        rtA.dispose(); rtB.dispose()
        if (renderer.domElement.parentNode === container) container.removeChild(renderer.domElement)
      }
    })()

    return () => { cancelled = true; cleanup() }
  }, [])

  useEffect(() => {
    const id = setInterval(() => setModelIndex((i) => (i + 1) % HERO_MODELS.length), 4000)
    return () => clearInterval(id)
  }, [])

  const m = HERO_MODELS[modelIndex]

  return (
    <>
      <div className="absolute inset-0 z-0" id="heroLiquid" ref={containerRef} aria-hidden="true" />
      <div
        className="pointer-events-none absolute left-1/2 -bottom-[3.5vh] z-0 -translate-x-1/2 font-extrabold text-[26vh] leading-[0.8] tracking-[-0.04em] text-[rgba(255,255,255,.035)] whitespace-nowrap select-none"
        aria-hidden="true"
      >
        LIQUIDBRAIN
      </div>
      <Link
        to="/models"
        className="group relative z-10 order-5 mt-[48px] px-[28px] text-left text-inherit no-underline transition-opacity duration-150 hover:opacity-80 sm:absolute sm:z-0 sm:order-none sm:right-[40px] sm:bottom-[40px] sm:mt-0 sm:px-0 sm:text-right"
        aria-label={`${m.soon ? 'Coming soon' : 'Now serving'} ${m.name} — see all models`}
      >
        <div className="mb-[10px] font-mono text-[11px] tracking-[0.12em] text-[#52525b] group-hover:text-[#93A9FF]">{m.soon ? 'COMING SOON' : 'NOW SERVING'}</div>
        <div className="flex items-center justify-start gap-[12px] sm:justify-end">
          <span className="h-[9px] w-[9px] flex-none rounded-full" style={{ background: m.color, boxShadow: `0 0 12px ${m.color}` }} />
          <span className="font-mono text-[22px] font-medium tracking-[-0.01em] text-[#fafafa]">{m.name}</span>
        </div>
        <div className="mt-[8px] font-mono text-[12px] text-[#71717a]">{m.sub}</div>
        <div className="mt-[18px] flex justify-start gap-[7px] sm:justify-end">
          {HERO_MODELS.map((mm, i) => (
            <span
              key={mm.name}
              className="h-[6px] w-[6px] rounded-full bg-[#3f3f46] transition-all duration-300"
              style={i === modelIndex ? { background: mm.color, boxShadow: `0 0 8px ${mm.color}`, transform: 'scale(1.25)' } : undefined}
            />
          ))}
        </div>
      </Link>
    </>
  )
}
